import MaterialTable, { Column, MaterialTableProps, Query, QueryResult  } from '@material-table/core';
import { ReactElement, useCallback, useRef, useEffect } from 'react';
import { tableIcons } from '../../../ts-components/app-supplier-database/icons';
import styles from './DataTable.module.scss';
import { createPortal } from 'react-dom';
import { useTranslation } from 'react-i18next';
import PaginationWithGoTo from '../../../ts-components/pagination-with-goto/pagination-with-goto';
import { CircularProgress } from '@mui/material';
import { alpha } from '@material-ui/core/styles';

export type CustomTableProps<T extends object> = MaterialTableProps<T>

type Props<T extends Object> = CustomTableProps<T> & {
  tableRef?: any,
  title?: string | ReactElement,
  isLoading: boolean,
  columns: Column<T>[],
  data: T[] | ((query: Query<object>) => Promise<QueryResult<T>>),
  onRowClick?: (
    event?: React.MouseEvent,
    rowData?: T,
    toggleDetailPanel?: (panelIndex?: number) => void
  ) => void,
  pageSize?: number,
  style?: React.CSSProperties,
  isScrollable?: boolean,
  sorting?: boolean,
  thirdSortClick?: boolean,
  defaultOrderBy?: number,
  defaultOrderDirection?: 'asc' | 'desc',
  defaultOrderByField?: string,
  onOrderChange?: (orderBy: number, orderDirection: 'asc' | 'desc', orderByField: string) => void,
  onPageChange?: (page: number, pageSize: number) => void,
  tableLayout?: 'auto' | 'fixed',
  rowStyle?: React.CSSProperties | ((data: T, index: number, level: number) => React.CSSProperties),
}

const DataTable = <T extends Object = {}>({
  tableRef,
  title,
  isLoading,
  columns,
  data,
  onRowClick,
  pageSize,
  style,
  isScrollable,
  sorting = true,
  thirdSortClick = true,
  defaultOrderBy = -1,
  defaultOrderDirection = 'asc',
  defaultOrderByField = '',
  onOrderChange,
  onPageChange,
  detailPanel,
  tableLayout = 'auto',
  rowStyle: rowStyleProp,
} : Props<T>) => {
  const pagination = useRef(null);
  const containerRef = useRef<HTMLDivElement>(null);

  const rowStyle = useCallback((data: T, index: number, level: number) => ({
    color: 'var(--gray-800)',
    ...(typeof rowStyleProp === 'function' ? rowStyleProp(data, index, level) : rowStyleProp),
  }), [rowStyleProp]);

  const handleOrderChange = useCallback((orderBy: number, orderDirection: 'asc' | 'desc') => {
    if (onOrderChange) {
      const column = columns[orderBy];
      const orderByField = column?.field?.toString() ?? '';
      onOrderChange(orderBy, orderDirection, orderByField);
    }
  }, [onOrderChange, columns]);

  const [t] = useTranslation('shared');
  const [tPagination] = useTranslation('pagination', { keyPrefix: 'pagination' });

  const hasPaging = !!(pageSize && pageSize > 0);

  // Call onPageChange on initial render with page 0
  useEffect(() => {
    if (onPageChange && pageSize && !isLoading && Array.isArray(data) && data.length > 0) {
      onPageChange(0, pageSize);
    }
  }, [isLoading, data]);

  let tableStyle: React.CSSProperties = { marginBottom: 0 };

  if (isScrollable) {
    tableStyle = {...tableStyle, minWidth: '100%', width: 'fit-content'};
  }

  return (<>
    <div ref={containerRef} style={style} className={`supplier-db-main-table-wrap ${styles.container} ${isScrollable ? styles.isScrollable : ''}`}>
      <MaterialTable<T>
        key={`${defaultOrderBy}-${defaultOrderDirection}-${defaultOrderByField}`}
        tableRef={tableRef}
        title={title}
        icons={tableIcons}
        style={tableStyle}
        isLoading={isLoading}
        options={{
          sorting: sorting,
          thirdSortClick: thirdSortClick,
          draggable: false,
          search: false,
          toolbar: false,
          paging: hasPaging,
          pageSize: pageSize,
          emptyRowsWhenPaging: false,
          detailPanelColumnStyle: { display: 'none' },
          ...(detailPanel ? {
            detailPanelType: 'single' as const,
            showDetailPanelIcon: false,
            detailPanelOffset: { left: 0, right: 1 },
          } : {}),
          rowStyle: rowStyle,
          columnResizable: false,
          tableLayout: tableLayout,
          defaultOrderByCollection: defaultOrderBy >= 0 ? [{
            orderBy: defaultOrderBy,
            orderDirection: defaultOrderDirection,
            sortOrder: 0,
            orderByField: defaultOrderByField || columns[defaultOrderBy]?.field?.toString() || ''
          }] : []
        }}
        localization={{ body: { emptyDataSourceMessage: t('dataTable.emptyMessage', 'Kunde inte hitta något resultat') } }}
        columns={columns}
        data={data}
        onRowClick={onRowClick}
        detailPanel={detailPanel}
        onOrderChange={handleOrderChange}
        components={{
          Pagination: ({page, count, onPageChange: materialOnPageChange}) => {
            if (!hasPaging) return null;
            return pagination.current && createPortal(
              <PaginationWithGoTo
                count={Math.ceil(count/(pageSize || 10))}
                onChange={(_, value) => {
                  materialOnPageChange(_, value - 1);
                  // Call the custom onPageChange callback if provided
                  if (onPageChange && pageSize) {
                    onPageChange(value - 1, pageSize);
                  }
                }}
                page={page + 1}
                t={tPagination}
              />, pagination.current
            );
          },
          OverlayLoading: (props) => {
            return (
              <div
                className={styles.overlayLoading}
                style={{ backgroundColor: alpha(props.theme.palette.background.paper, 0.7) }}
              >
                <div className={styles.overlayLoadingCell}>
                  <CircularProgress />
                </div>
              </div>
            );
          }
        }}
      />
    </div>
    {hasPaging && <div ref={pagination} className={`sup-pagination org-pagination ${styles.pagination}`}></div>}
    </>
  );
}

export default DataTable;
